(ns common.transaction-process.access
  (:require
   [common.data-types :as data-types]
   [common.features.plan :as plan]
   [common.roles :as roles]
   [common.schema.decision-types :as decision-types]
   [common.schema.entities.transaction-process :as tp-schema]
   [common.transaction-process.config :as tp-config]))

(def ^:private sub-entity-types
  "Entity types a transaction-process holds in its named sub-entity slots."
  (conj decision-types/decision-entity-types
        :entity.type/attestation
        :entity.type/declaration))

(def sub-entity-slot-attrs
  "The named cardinality-one ref slots a transaction-process holds its
   decisions / attestations / declarations in — every ref-single field on the TP
   targeting a sub-entity type. Derived from the schema, so a new slot is picked
   up automatically."
  (->> tp-schema/schema
       (keep (fn [[attr fs]]
               (when (and (= :data.type/ref-single (:field/data-type fs))
                          (some sub-entity-types (:field/entity-types fs)))
                 attr)))
       vec))

(def sub-entity-slot-attr?
  (set sub-entity-slot-attrs))

(def ^:private sub-entity-slot-reverse-refs
  (mapv (fn [attr] (keyword "transaction-process" (str "_" (name attr))))
        sub-entity-slot-attrs))

(defn find-parent-transaction-process
  "The (non-disabled) transaction-process that holds `sub-entity` in one of its
   named slots, discovered by scanning the named-slot reverse-refs.
   Returns the TP entity or nil."
  [sub-entity]
  (some (fn [rev]
          (->> (get sub-entity rev)
               (remove :common/disabled?)
               first))
        sub-entity-slot-reverse-refs))

(defn parse-team-assignments
  "Parse a team-assignments map (or the raw EDN string stored in
  :transaction-process/team-assignments-edn). Returns a map of
  role-id -> members."
  [raw]
  (cond
    (map? raw)    raw
    (string? raw) (let [parsed (try (data-types/parse-edn-string raw)
                                    (catch #?(:clj Exception :cljs :default) _ nil))]
                    (if (map? parsed) parsed {}))
    :else         {}))

(defn account-in-team-assignments?
  "True when `account-id` appears in any role of the given team-assignments map."
  [team-assignments account-id]
  (->> (vals (or team-assignments {}))
       (some (fn [members]
               (some (fn [m] (= account-id (if (map? m) (:value m) m)))
                     members)))
       boolean))

(defn account-in-fund-teams?
  "True when any of the user's team memberships is also one of the vehicle's
  active :fund/teams."
  [vehicle account-team-memberships]
  (let [vehicle-team-ids (->> vehicle
                              :fund/teams
                              (remove :common/disabled?)
                              (map (fn [t] (or (:db/id t) t)))
                              (remove nil?)
                              set)]
    (boolean (some (set account-team-memberships) vehicle-team-ids))))

(defn account-in-template-defaults?
  "True when `account-id` appears in the fund's
  :tp-config/default-team-members[template-kw] under any role."
  [{:keys [vehicle-config template-kw account-id]}]
  (-> (get-in vehicle-config [:tp-config/default-team-members template-kw])
      (account-in-team-assignments? account-id)))

(defn can-access-transaction-process?
  "Canonical read-access predicate for :entity.type/transaction-process.

   Admin users with the feature flag enabled always pass. Non-admin users
   must have the feature + module enabled and be assigned to at least one
   role of the TP's :transaction-process/team-assignments-edn.

   Create access remains stricter and still validates fund-team/default-team
   membership against the vehicle template config. Once a process exists, the
   transaction's own team assignment is the source of truth for detail access."
  [{:keys [tenant tenant-plan role account account-current-permissions entity]}]
  (and (plan/available :feature/transaction-process tenant nil tenant-plan)
       (or (roles/admin? role)
           (and (contains? (:account-modules account-current-permissions)
                           :module/transaction-process)
                (let [account-id       (:db/id account)
                      team-assignments (parse-team-assignments
                                        (:transaction-process/team-assignments-edn entity))]
                  (account-in-team-assignments? team-assignments account-id))))))

(defn can-create-transaction-process?
  "Create-gate predicate. Admin with feature flag passes. Non-admin must
  have module on, be in the vehicle's :fund/teams, AND be listed in the
  vehicle's :tp-config/default-team-members for the specific template-kw."
  [{:keys [tenant tenant-plan role account account-current-permissions vehicle template-kw]}]
  (and (plan/available :feature/transaction-process tenant nil tenant-plan)
       (or (roles/admin? role)
           (and (contains? (:account-modules account-current-permissions)
                           :module/transaction-process)
                (let [account-id    (:db/id account)
                      account-teams (set (:account-team-memberships account-current-permissions))
                      vehicle-config (tp-config/parse-vehicle-config
                                      (:fund/transaction-process-config vehicle))]
                  (and (account-in-fund-teams? vehicle account-teams)
                       (account-in-template-defaults?
                        {:vehicle-config vehicle-config
                         :template-kw    template-kw
                         :account-id     account-id})))))))
